import { computed } from "vue";
import { useRouter } from "vue-router";
import { isMessagingEnabled } from "@/config/messaging";
import { useCursoStore } from "@/store/cursoStore";
import { ensureMySupportConversations, } from "@/services/messagingApiService";
import { useMessagingStore } from "@/store/messagingStore";
import { isAcademicStaffCourse } from "@/utils/funcionarios";
import { isFuncionariosCourse, isKioskCourse } from "@/utils/parseCourseCode";
const RETURN_CONTEXT_KEY = "messagingReturnContext";
const courseCodeOf = (curso) => String(curso?.course_code || curso?.course_id || "").trim();
/** Visible si mensajería está habilitada. Sin comunidad también (Avisos + Soporte). */
export const canAccessMessaging = (curso = null) => {
    if (!isMessagingEnabled())
        return false;
    const cursoStore = useCursoStore();
    const active = curso || cursoStore.getCursoData();
    if (!active)
        return true;
    if (isKioskCourse(active))
        return false;
    if (isFuncionariosCourse(active) &&
        !isAcademicStaffCourse(active)) {
        return false;
    }
    return true;
};
export const saveMessagingReturnContext = (context) => {
    try {
        sessionStorage.setItem(RETURN_CONTEXT_KEY, JSON.stringify(context));
    }
    catch {
        // ignore quota
    }
};
export const readMessagingReturnContext = () => {
    try {
        const raw = sessionStorage.getItem(RETURN_CONTEXT_KEY);
        if (!raw)
            return null;
        return JSON.parse(raw);
    }
    catch {
        return null;
    }
};
export const useMessagingNavigation = () => {
    const router = useRouter();
    const cursoStore = useCursoStore();
    const messagingAvailable = computed(() => canAccessMessaging((cursoStore.selectedCursoData || cursoStore.getCursoData())));
    const openMessaging = (params) => {
        const curso = cursoStore.getCursoData();
        if (!canAccessMessaging(curso))
            return;
        const returnUrl = params?.returnUrl ||
            router.currentRoute.value.fullPath ||
            "/user/main-view";
        saveMessagingReturnContext({
            source: params?.source || "group-dashboard",
            schoolId: curso?.school_id,
            year: curso?.year,
            courseCode: courseCodeOf(curso),
            courseId: String(curso?.course_id || curso?.course_code || ""),
            returnRouteName: "main-view",
            returnUrl,
        });
        if (params?.conversationId) {
            void router.push({
                name: "messaging-conversation",
                params: { conversationId: params.conversationId },
            });
            return;
        }
        void router.push({ name: "messaging-conversations" });
    };
    const openSupportConversation = async (params) => {
        const curso = cursoStore.getCursoData();
        if (!canAccessMessaging(curso))
            return;
        const returnUrl = params?.returnUrl ||
            router.currentRoute.value.fullPath ||
            "/user/main-view";
        saveMessagingReturnContext({
            source: params?.source || "menu",
            schoolId: curso?.school_id,
            year: curso?.year,
            courseCode: courseCodeOf(curso),
            courseId: String(curso?.course_id || curso?.course_code || ""),
            returnRouteName: "main-view",
            returnUrl,
        });
        try {
            const ensured = await ensureMySupportConversations();
            const store = useMessagingStore();
            const supportId = String(ensured.supportConversationId || "").trim() ||
                store.conversations.find((item) => String(item.type || "").toUpperCase() === "SUPPORT" ||
                    String(item.conversationId || "").startsWith("support:"))?.conversationId;
            if (!supportId) {
                void router.push({ name: "messaging-conversations" });
                return;
            }
            store.upsertConversation({
                id: supportId,
                conversationId: supportId,
                title: "Soporte",
                name: "Soporte",
                courseCode: "SP",
                type: "SUPPORT",
            });
            const draft = String(params?.draft || "").trim();
            void router.push({
                name: "messaging-conversation",
                params: { conversationId: supportId },
                query: draft ? { draft } : undefined,
            });
        }
        catch {
            void router.push({ name: "messaging-conversations" });
        }
    };
    const goBackFromMessaging = () => {
        const ctx = readMessagingReturnContext();
        if (ctx?.returnRouteName) {
            void router.push({ name: ctx.returnRouteName });
            return;
        }
        if (cursoStore.getCursoData()) {
            void router.push({ name: "main-view" });
            return;
        }
        if (window.history.length > 1) {
            router.back();
            return;
        }
        void router.push({ name: "main-view" });
    };
    return {
        messagingAvailable,
        openMessaging,
        openSupportConversation,
        goBackFromMessaging,
        canAccessMessaging,
    };
};
